import { onDocumentLoaded, onDocumentReady } from '@theme/utilities';

const CLOSE_DELAY_MS = 200;
const STICKY_HIDE_DELAY_MS = 150;
const DRAWER_SEARCH_PANEL_OPEN = 'data-drawer-search-panel-open';

/**
 * Wire the mobile menu trigger (burger) to open the header drawer.
 * The drawer uses the same <header-drawer> component; we set details[open] and call open().
 * @param {HTMLElement} header
 */
const initNewHeaderDrawer = (header) => {
  const trigger = header.querySelector('.new-header__mobile-menu-trigger');
  const searchTrigger = header.querySelector('.new-header__search-trigger');
  const drawer = /** @type {HTMLElement & { isOpen?: boolean, open?: () => void, close?: () => void } | null} */ (
    header.querySelector('#new-header-menu-drawer')
  );
  const details = drawer?.querySelector('details');
  if (!drawer || !details) return;

  const getDrawerSearchWrap = () => drawer.querySelector('.new-header__drawer-search-predictive');
  /** @type {boolean} */
  let drawerSearchUserInteracted = false;

  const openDrawerSearchPanel = () => {
    const searchWrap = getDrawerSearchWrap();
    searchWrap?.setAttribute(DRAWER_SEARCH_PANEL_OPEN, '');
  };

  const closeDrawerSearch = () => {
    const searchWrap = getDrawerSearchWrap();
    if (!(searchWrap instanceof HTMLElement)) return;

    searchWrap.removeAttribute(DRAWER_SEARCH_PANEL_OPEN);

    const input = searchWrap.querySelector('input[type="search"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = '';
    input.blur();

    const api = /** @type {any} */ (searchWrap);
    api.resetSearch?.(false);
  };

  drawer.addEventListener(
    'pointerdown',
    (e) => {
      if (!(e.target instanceof Element)) return;
      const searchWrap = getDrawerSearchWrap();
      if (!(searchWrap instanceof HTMLElement)) return;
      if (!searchWrap.contains(e.target)) return;
      drawerSearchUserInteracted = true;
    },
    true
  );

  drawer.addEventListener('focusin', (e) => {
    if (!(e.target instanceof Element)) return;
    const searchWrap = e.target.closest('.new-header__drawer-search-predictive');
    if (!searchWrap || !drawer.contains(searchWrap)) return;

    const searchInput = searchWrap.querySelector('input[type="search"]');
    const hasQuery = searchInput instanceof HTMLInputElement && searchInput.value.trim().length > 0;
    if (!drawerSearchUserInteracted && !hasQuery) return;

    drawerSearchUserInteracted = false;
    openDrawerSearchPanel();
  });

  document.addEventListener(
    'pointerdown',
    (e) => {
      if (!(e.target instanceof Node)) return;
      const searchWrap = getDrawerSearchWrap();
      if (!(searchWrap instanceof HTMLElement)) return;
      if (!searchWrap.hasAttribute(DRAWER_SEARCH_PANEL_OPEN)) return;
      if (!details.hasAttribute('open')) return;
      if (searchWrap.contains(e.target)) return;
      closeDrawerSearch();
    },
    true
  );

  details.addEventListener('toggle', () => {
    if (!details.hasAttribute('open')) {
      getDrawerSearchWrap()?.removeAttribute(DRAWER_SEARCH_PANEL_OPEN);
    }
  });

  /**
   * `header-drawer.js` is a separate module, so `<header-drawer>` may not have upgraded yet
   * when an early tap lands. Resolving this before opening keeps the drawer's own close
   * paths (backdrop, top-row X, Escape) wired, so the drawer can never open un-closeable.
   * @returns {Promise<void>}
   */
  const whenDrawerReady = () => {
    if (typeof drawer.open === 'function') return Promise.resolve();
    return customElements.whenDefined('header-drawer').then(() => undefined);
  };

  /**
   * Open the drawer, honouring taps that land before the drawer module is ready.
   * @returns {Promise<void>}
   */
  const openDrawer = async () => {
    // Read the attribute rather than `drawer.isOpen`, which only exists once upgraded.
    if (details.hasAttribute('open')) return;

    await whenDrawerReady();
    if (details.hasAttribute('open')) return;

    details.setAttribute('open', '');
    drawer.open?.();
  };

  const focusDrawerSearchInput = () => {
    const maxAttempts = 8;
    let attempts = 0;

    const tryFocus = () => {
      const searchInput = drawer.querySelector('.new-header__drawer-search-predictive input[type="search"]');
      if (searchInput instanceof HTMLInputElement) {
        searchInput.focus({ preventScroll: true });
        if (document.activeElement === searchInput) return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) return;
      window.setTimeout(tryFocus, 50);
    };

    // Run one frame later to wait for drawer DOM/animation state.
    requestAnimationFrame(tryFocus);
  };

  // Tell the inline pre-hydration bootstrap in `sections/header.liquid` to stand down:
  // from here on this module owns the trigger, so only one handler is ever live.
  header.setAttribute('data-header-wired', '');

  // The bootstrap may already have opened the drawer with a bare `details[open]`. Adopt
  // it so the component's scroll lock, focus trap and close paths engage retroactively.
  if (details.hasAttribute('open')) {
    whenDrawerReady().then(() => {
      if (details.hasAttribute('open')) drawer.open?.();
    });
  }

  trigger?.addEventListener('click', async () => {
    if (!details.hasAttribute('open')) {
      await openDrawer();
      return;
    }

    await whenDrawerReady();
    drawer.close?.();
  });

  searchTrigger?.addEventListener('click', async () => {
    await openDrawer();
    focusDrawerSearchInput();
    openDrawerSearchPanel();
  });
};

/**
 * @param {HTMLElement} root
 */
const initNewHeaderMegaMenu = (root) => {
  if (!(root instanceof HTMLElement)) return;

  const columns = Array.from(root.querySelectorAll('[data-menu-column]')).filter(
    (col) => col instanceof HTMLElement
  );
  if (columns.length === 0) return;

  const isDesktop = () => window.matchMedia?.('(min-width: 1024px)').matches ?? false;

  const closeDesktopSearch = ({ keepTerm = false } = {}) => {
    if (!isDesktop()) return;

    const searchWrap = root.querySelector('.new-header__search-desktop-predictive');
    if (!(searchWrap instanceof HTMLElement)) return;

    const input = searchWrap.querySelector('input[type="search"]');
    if (!(input instanceof HTMLInputElement)) return;

    const searchOpen = input.value.trim().length > 0 || searchWrap.matches(':focus-within');
    if (!searchOpen) return;

    if (keepTerm && input.value.trim().length > 0) {
      searchWrap.setAttribute('data-search-panel-hidden', '');
      input.blur();
      return;
    }

    // Hide panel immediately (CSS is driven by input placeholder/values).
    input.value = '';
    input.blur();

    // Reset results markup after debounce.
    const api = /** @type {any} */ (searchWrap);
    api.resetSearch?.(false);
  };

  const desktopSearchWrap = root.querySelector('.new-header__search-desktop-predictive');
  if (desktopSearchWrap instanceof HTMLElement) {
    desktopSearchWrap.addEventListener('focusin', () => {
      desktopSearchWrap.removeAttribute('data-search-panel-hidden');
    });
  }

  /** @type {number | null} */
  let closeTimer = null;

  const closeAll = () => {
    columns.forEach((col) => {
      const trigger = col.querySelector('[data-menu-column-trigger]');
      const panel = col.querySelector('[data-menu-column-panel]');
      if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'false');
      if (panel instanceof HTMLElement) panel.hidden = true;
    });
  };

  /**
   * @param {HTMLElement} col
   */
  const openColumn = (col) => {
    if (!(col instanceof HTMLElement)) return;
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    closeAll();

    const trigger = col.querySelector('[data-menu-column-trigger]');
    const panel = col.querySelector('[data-menu-column-panel]');
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'true');
    if (panel instanceof HTMLElement) panel.hidden = false;
  };

  const scheduleCloseAll = () => {
    if (closeTimer !== null) window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(closeAll, CLOSE_DELAY_MS);
  };

  columns.forEach((col) => {
    col.addEventListener('pointerenter', () => {
      closeDesktopSearch({ keepTerm: true });
      openColumn(/** @type {HTMLElement} */ (col));
    });
    col.addEventListener('pointerleave', scheduleCloseAll);
    col.addEventListener('focusin', () => {
      closeDesktopSearch();
      openColumn(/** @type {HTMLElement} */ (col));
    });
    col.addEventListener('focusout', scheduleCloseAll);
  });

  // When the desktop predictive search is open, hovering any menu link should close it.
  root.addEventListener('pointerover', (e) => {
    if (!isDesktop()) return;
    if (!(e.target instanceof Element)) return;

    const searchWrap = root.querySelector('.new-header__search-desktop-predictive');
    if (!(searchWrap instanceof HTMLElement)) return;

    const input = searchWrap.querySelector('input[type="search"]');
    if (!(input instanceof HTMLInputElement)) return;

    const searchOpen = input.value.length > 0 || searchWrap.matches(':focus-within');
    if (!searchOpen) return;

    const menuLink = e.target.closest('.new-header__menu-link, [data-menu-column-trigger], .new-header__block-title');
    if (!menuLink) return;

    closeDesktopSearch({ keepTerm: true });
  });

  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeAll();
  });

  document.addEventListener('pointerdown', (e) => {
    if (!(e.target instanceof Node)) return;

    // Desktop: if the click is outside the header search widget, close predictive search.
    if (isDesktop()) {
      const searchWrap = root.querySelector('.new-header__search-desktop-predictive');
      if (searchWrap instanceof HTMLElement && !searchWrap.contains(e.target)) {
        closeDesktopSearch();
      }
    }

    if (root.contains(e.target)) return;
    closeAll();
  });
};

/**
 * Keep legacy header CSS variables available for dependent components.
 * @param {HTMLElement} header
 */
const initHeaderSizingVars = (header) => {
  const updateHeaderHeight = () => {
    const roundedHeaderHeight = Math.round(header.getBoundingClientRect().height || header.offsetHeight || 0);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
  };

  const headerObserver = new ResizeObserver(() => {
    updateHeaderHeight();
  });
  headerObserver.observe(header);
  updateHeaderHeight();

  const headerGroup = document.querySelector('#header-group');
  if (!(headerGroup instanceof HTMLElement)) return;

  const updateHeaderGroupHeight = () => {
    const totalHeight = Array.from(headerGroup.children).reduce((sum, child) => {
      if (!(child instanceof HTMLElement)) return sum;
      return sum + child.getBoundingClientRect().height;
    }, 0);

    document.body.style.setProperty('--header-group-height', `${Math.round(totalHeight)}px`);
  };

  const groupObserver = new ResizeObserver(() => {
    updateHeaderGroupHeight();
  });

  groupObserver.observe(headerGroup);
  Array.from(headerGroup.children).forEach((child) => {
    if (child instanceof HTMLElement) groupObserver.observe(child);
  });

  const mutationObserver = new MutationObserver(() => {
    Array.from(headerGroup.children).forEach((child) => {
      if (child instanceof HTMLElement) groupObserver.observe(child);
    });
    updateHeaderGroupHeight();
  });

  mutationObserver.observe(headerGroup, { childList: true });
  updateHeaderGroupHeight();
};

/**
 * Re-introduce legacy sticky behavior options: always / scroll-up / never.
 * @param {HTMLElement} header
 */
const initHeaderSticky = (header) => {
  const stickyMode = header.getAttribute('sticky');
  if (!stickyMode) return;

  const mobileStickyQuery = window.matchMedia ? window.matchMedia('(max-width: 1023px)') : null;
  let offscreen = false;
  let lastScrollTop = 0;
  /** @type {number | null} */
  let timeout = null;
  /** @type {number | null} */
  let scrollRafId = null;
  /** @type {IntersectionObserver | null} */
  let intersectionObserver = null;

  const shouldForceMobileSticky = () => mobileStickyQuery?.matches ?? false;

  const updateScrollState = () => {
    if (!offscreen && stickyMode !== 'always') return;

    if (shouldForceMobileSticky()) {
      header.dataset.stickyState = 'active';
      header.dataset.scrollDirection = 'none';
      return;
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = header.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        header.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        header.dataset.scrollDirection = 'up';
      } else {
        header.dataset.scrollDirection = 'down';
      }
      header.dataset.stickyState = 'active';
      lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      header.removeAttribute('data-animating');
      if (isAtTop) {
        offscreen = false;
        header.dataset.stickyState = 'inactive';
        header.dataset.scrollDirection = 'none';
      } else {
        header.dataset.stickyState = 'active';
        header.dataset.scrollDirection = 'up';
      }
    } else if (header.dataset.stickyState === 'active') {
      header.dataset.scrollDirection = 'none';
      header.setAttribute('data-animating', '');
      timeout = window.setTimeout(() => {
        header.dataset.stickyState = 'idle';
        header.removeAttribute('data-animating');
      }, STICKY_HIDE_DELAY_MS);
    } else {
      header.dataset.scrollDirection = 'none';
      header.dataset.stickyState = 'idle';
    }

    lastScrollTop = scrollTop;
  };

  const handleWindowScroll = () => {
    if (scrollRafId !== null) return;
    scrollRafId = requestAnimationFrame(() => {
      scrollRafId = null;
      updateScrollState();
    });
  };

  const observeStickyPosition = (alwaysSticky = true) => {
    if (intersectionObserver) return;
    intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (shouldForceMobileSticky()) {
          header.dataset.stickyState = 'active';
          header.dataset.scrollDirection = 'none';
          return;
        }

        if (alwaysSticky) {
          header.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
        } else {
          offscreen = !entry.isIntersecting || header.dataset.stickyState === 'active';
        }
      },
      { threshold: alwaysSticky ? 1 : 0 }
    );

    intersectionObserver.observe(header);
  };

  const handleMobileStickyChange = () => {
    if (shouldForceMobileSticky()) {
      header.dataset.stickyState = 'active';
      header.dataset.scrollDirection = 'none';
      offscreen = true;
      return;
    }

    offscreen = false;
    if (header.dataset.stickyState === 'active') {
      header.dataset.stickyState = 'inactive';
    }

    intersectionObserver?.disconnect();
    intersectionObserver = null;
    observeStickyPosition(stickyMode === 'always');
    updateScrollState();
  };

  observeStickyPosition(stickyMode === 'always');
  if (stickyMode === 'scroll-up' || stickyMode === 'always') {
    document.addEventListener('scroll', handleWindowScroll);
  }

  mobileStickyQuery?.addEventListener('change', handleMobileStickyChange);
  handleMobileStickyChange();
};

/**
 * Interactive wiring must not wait for `load`. On PLP/PDP the window `load` event lands
 * seconds after the header is painted and tappable (product imagery, third-party tags),
 * so the mobile/tablet menu + search trigger had no click listener yet and the first tap
 * was silently dropped — the user had to tap again once the page finished loading.
 */
onDocumentReady(() => {
  document.querySelectorAll('.new-header').forEach((header) => {
    if (!(header instanceof HTMLElement)) return;
    initNewHeaderMegaMenu(header);
    initNewHeaderDrawer(header);
  });
});

/** Layout measurement and sticky observers stay on `load`, where they were. */
onDocumentLoaded(() => {
  document.querySelectorAll('.new-header').forEach((header) => {
    if (!(header instanceof HTMLElement)) return;
    initHeaderSizingVars(header);
    initHeaderSticky(header);
  });
});
